<script setup lang="ts">
import type { GameVehicleManufacturer } from '~~/types/gamedata';

const props = defineProps<{
  manufacturer: GameVehicleManufacturer;
}>();

const runtimeConfig = useRuntimeConfig();

const companyHref = computed(() => props.manufacturer.companySlug
  ? `/verseexkurs/companies/${props.manufacturer.companySlug}`
  : undefined);

const backgroundUrl = computed(() => {
  const asset = props.manufacturer.logoBackground;
  if (!asset) return undefined;
  if (/^https?:\/\//.test(asset)) return asset;

  const baseUrl = String(runtimeConfig.public.ASSETS_URL).replace(/\/$/, '');
  return `${baseUrl}/${asset.replace(/^\//, '')}`;
});
</script>

<template>
  <!-- Das native <a> erzwingt den vollständigen Wechsel zur Legacy-Nuxt-Seite. -->
  <component
    :is="companyHref ? 'a' : 'div'"
    :href="companyHref"
    :aria-label="companyHref ? `${manufacturer.name ?? 'Hersteller'} öffnen` : undefined"
    class="group relative flex h-36 items-center justify-center overflow-hidden rounded-lg border border-default bg-bunker-950 px-8 transition-colors hover:border-primary/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
  >
    <video
      v-if="backgroundUrl"
      :src="backgroundUrl"
      autoplay
      muted
      loop
      playsinline
      preload="metadata"
      aria-hidden="true"
      class="pointer-events-none absolute inset-0 size-full object-cover opacity-0 transition-opacity duration-300 group-hover:opacity-70 group-focus-visible:opacity-70"
    />
    <div
      class="pointer-events-none absolute inset-0 bg-gradient-to-t from-bunker-950 via-bunker-950/35 to-bunker-950/70 transition-colors duration-300 group-hover:via-bunker-950/10"
      aria-hidden="true"
    />

    <NuxtImg
      v-if="manufacturer.logo"
      :src="manufacturer.logo"
      :alt="manufacturer.name ? `Logo von ${manufacturer.name}` : 'Herstellerlogo'"
      class="relative z-10 max-h-20 w-full max-w-64 object-contain drop-shadow-lg transition-transform duration-300 group-hover:scale-105 group-focus-visible:scale-105"
    />
    <span v-else class="relative z-10 text-center text-lg font-semibold text-highlighted">
      {{ manufacturer.name ?? manufacturer.code ?? 'Unbekannter Hersteller' }}
    </span>

    <span
      v-if="companyHref"
      class="absolute right-3 bottom-2 z-10 flex items-center gap-1 text-xs text-muted opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
    >
      Hersteller ansehen
      <UIcon name="i-lucide-arrow-up-right" class="size-3.5" />
    </span>
  </component>
</template>
